import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";

import TaskButton, { Status } from "@/components/base/button/TaskButton";
import {
  TaskAwardResponse,
  TaskList,
  TaskStatus,
  TaskStatusResponse,
  TaskVariant,
} from "../tasks.types";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/utils";
import { TaskKeys } from "../tasks.api";
import { boom } from "@/lib/exposion";
import { useStoryDetail } from "@/pages/home/api";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { useLocation, useNavigate } from "react-router";
import { openLink, openTelegramLink } from "@telegram-apps/sdk-react";
import { useFetch } from "@/lib/axios.config";
import { sleep } from "@/shared/utils/sleep";
import {
  isNullOrUndefined,
  openTelegramUsernameLink,
} from "@/shared/utils/common";
import TaskStoryModal from "@/components/stories/TaskStoryModal";
import PremiumAlertDialog from "@/components/common/dialog/PremiumAlertDialog";
import { useGetProfile } from "@/pages/profile/api";
import { TFunction } from "i18next";
import PromocodeSheet from "./PromocodeDrawer";
import { resolveFarmTaskStatus } from "./farmTaskStatus";
import { PaginationResponse } from "@/shared/types/api.types";
import { appConfig } from "@/lib/site-config";

const verifyTask = async (task: TaskList) => {
  const response = await useFetch.post<{ state: TaskStatusResponse }>(
    `/tasks/ClaimTaskAward/${task.id}`,
    {
      keyword: "",
    }
  );
  return response.data.state;
};

interface TaskActionButtonProps {
  task: TaskList;
  onStatusUpdate?: (status: TaskStatusResponse) => void;
  collectionId?: string;
  selectedCategoryId?: string | null;
}

// Error handler Component
type ErrorHandlerProps = {
  error: Error;
};

function ErrorHandler(props: ErrorHandlerProps) {
  return <h3 className="text-red text-sm">{props.error.message}</h3>;
}

const TaskActionButton: React.FC<TaskActionButtonProps> = (props) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler}>
      <BaseTaskActionButton {...props} />
    </ErrorBoundary>
  );
};

// @ts-expect-error i know this
const statusOrder: Record<Status, number> = {
  INCOMPLETE: 0,
  PERFORMED: 1,
  READY_AWARD: 2,
  COMPLETED: 3,
  PENDING: 0, // PENDING is a temporary state, not persisted
};

type TaskContext = {
  task: TaskList;
  setStatus: (status: Status) => void;
  router: ReturnType<typeof useNavigate>;
  showStories: () => void;
  getDonAward: () => Promise<void>;
  t: TFunction<"translation", undefined>;
};

type TaskStrategy = {
  perform: (ctx: TaskContext) => Promise<void>;
  isRequiredConfirmation?: boolean;
  shouldVerifyBeforePerform?: boolean;
};

const handleOpenLink = (link: string) => {
  if (link.startsWith(appConfig.telegram.baseUrl)) {
    return openTelegramLink(link);
  }

  openLink(link);
};

const taskStrategies: Partial<Record<TaskVariant, TaskStrategy>> = {
  [TaskVariant.ViewStory]: {
    async perform({ showStories }) {
      showStories();
      // setTimeout(() => setStatus(""), 100);
    },
    shouldVerifyBeforePerform: true,
  },
  [TaskVariant.GotoLink]: {
    async perform({ task, setStatus }) {
      if (task.link) {
        handleOpenLink(task.link);
      }
      await sleep(20000);
      setStatus("READY_AWARD");
    },
  },
  [TaskVariant.Keyword]: {
    async perform({ task, setStatus, router }) {
      if (task.link) {
        setStatus("PERFORMED");
        handleOpenLink(task.link);
      } else {
        router(`/get-don/${task?.id}/promo-code`);
      }
    },
    isRequiredConfirmation: true,
  },
  [TaskVariant.TelegramChatJoin]: {
    async perform({ setStatus, task }) {
      openTelegramUsernameLink(task.telegram_chat_id);
      setStatus("PERFORMED");
    },
    isRequiredConfirmation: true,
    shouldVerifyBeforePerform: true,
  },
  [TaskVariant.ExternalTask]: {
    async perform({ setStatus, task }) {
      handleOpenLink(task.link || "");
      setStatus("PERFORMED");
      await sleep(1000);
    },
    isRequiredConfirmation: true,
  },
  [TaskVariant.OptionTask]: {
    // Tashqi ko'rinishi ExternalTask bilan bir xil, lekin o'z obyekti:
    // ExternalTask strategiyasi o'zgarmasligi kerak.
    async perform({ setStatus, task }) {
      handleOpenLink(task.link || "");
      setStatus("PERFORMED");
      await sleep(1000);
    },
    isRequiredConfirmation: true,
  },
  [TaskVariant.Ads]: {
    async perform({ getDonAward, task, setStatus }) {
      // toast.info(t("maintenance_mode"));
      console.log("ads")
      handleOpenLink(task.link || "");
      await sleep(10000);
      setStatus("PERFORMED");
      getDonAward();
    },
  },
};

const BaseTaskActionButton: React.FC<TaskActionButtonProps> = ({
  task,
  collectionId,
  selectedCategoryId,
}) => {
  const storiesModal = useRef(null);
  const location = useLocation();
  const storageKey = useMemo(
    () => `taskStatus-${task.id}-${collectionId || "default"}`,
    [task.id, collectionId]
  );
  const { t } = useTranslation();
  const [currentStatus, setCurrentStatus] = useState<Status>(() => {
    const storedStatus = localStorage.getItem(storageKey) as Status;
    const removedPendingStatus =
      storedStatus === "PENDING" ? undefined : storedStatus;
    return removedPendingStatus ?? task.user_process?.status ?? "INCOMPLETE";
  });
  const router = useNavigate();
  const queryClient = useQueryClient();
  const { data: profileData } = useGetProfile();
  const [isProcessing, setIsProcessing] = useState(false);
  const isOptionTask = task.task_type === TaskVariant.OptionTask;
  // What the last GetTaskAward call actually credited, for the toast.
  const lastAwardRef = useRef<number | string | null>(null);

  const { data: storyData, isLoading: isStoryLoading } = useStoryDetail(
    task.story_id
  );

  useEffect(() => {
    if (currentStatus === "COMPLETED") {
      localStorage.removeItem(storageKey);
    } else if (currentStatus !== "PENDING") {
      localStorage.setItem(storageKey, currentStatus);
    }
  }, [currentStatus, storageKey]);

  const strategy = useMemo(() => {
    return taskStrategies[task.task_type];
  }, [task.task_type]);

  useEffect(() => {
    const serverStatus = task.user_process?.status as Status | undefined;
    if (serverStatus) {
      const serverOrder = statusOrder[serverStatus] ?? 0;
      const currentOrder = statusOrder[currentStatus] ?? 0;
      if (serverOrder > currentOrder) {
        setCurrentStatus(serverStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.user_process?.status]);

  const showStories = useCallback(() => {
    if (isStoryLoading) return;
    if (storiesModal.current) {
      // @ts-expect-error i know this
      storiesModal.current.openStories(0);
    }
  }, [isStoryLoading]);

  const { mutateAsync: claimReward, isPending: isClaiming } = useMutation({
    mutationFn: () => verifyTask(task),
    onSuccess(data) {
      setCurrentStatus(data);
      if (["INCOMPLETE", "RETRY_LATER"].includes(data)) {
        showCustomToast(t("incomplete_the_task"), "error");
      }
    },
  });

  const { mutateAsync: getAward, isPending: isVerifying } = useMutation({
    mutationFn: async () => {
      const response = await useFetch.post<{
        state: TaskAwardResponse;
        award?: number | string | null;
      }>(`/tasks/GetTaskAward/${task.id}`);
      // An option task pays per purchase, so the credited amount depends on
      // which option the partner reported — task.award is only a headline.
      lastAwardRef.current = response.data.award ?? null;
      // NOT_READY means the server credited nothing. Marking the task done
      // anyway left the user staring at a disabled "done" button for the rest
      // of the session, with no hint that the DON never arrived.
      if (
        response.data.state === "COMPLETED" ||
        response.data.state === "ALREADY_COMPLETED"
      ) {
        // An option task is repeatable: the next purchase has to find the card
        // open again, so it never settles on COMPLETED.
        setCurrentStatus(isOptionTask ? "INCOMPLETE" : "COMPLETED");
      }
      return response.data.state;
    },
    onSuccess: (data) => {
      if (data === "COMPLETED") {
        showCustomToast(t("congratulations"), "success", {
          description: t("get_done_successfully", {
            don: formatNumber(lastAwardRef.current ?? task.award),
          }),
          icon: <GetDoneSuccessIcon className="size-7" />,
        });
        boom();

        if (isOptionTask) {
          // The card stays open, so its server state (and any further pending
          // purchase) has to be refetched rather than left on the old page.
          queryClient.invalidateQueries({ queryKey: [TaskKeys.TaskList] });
        }

        if (
          !isNullOrUndefined(selectedCategoryId) &&
          [TaskVariant.Keyword, TaskVariant.Invites, TaskVariant.Farm].includes(
            task.task_type
          )
        ) {
          const queryKey = [TaskKeys.TaskList, selectedCategoryId];
          queryClient.setQueryData<InfiniteData<PaginationResponse<TaskList>>>(
            queryKey,
            (oldData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  results: page.results.map((t) => {
                    if (t.id === task.id) {
                      return {
                        ...t,
                        user_process: {
                          ...t.user_process,
                          status: TaskStatus.Completed,
                          completed_at: new Date().toISOString(),
                          user_message: "just updated",
                          cur_stage_value: t.user_process?.cur_stage_value ?? null,
                        },
                      };
                    }
                    return t;
                  }),
                })),
              };
            }
          );
        }

        queryClient.invalidateQueries({
          queryKey: ["profile"],
        });

        if (location.pathname === "/") {
          queryClient.invalidateQueries({
            queryKey: ["navigation"],
          });
        }

        if (collectionId) {
          queryClient.invalidateQueries({
            queryKey: [TaskKeys.TaskDetail, collectionId],
          });
        }
      } else if (data === "NOT_READY") {
        // The tap has to say something — silence here reads as a dead button.
        showCustomToast(t("incomplete_the_task"), "error");
      }
    },
  });

  const getAwardHandler = async () => {
    // if required confirmation is required, that mean, it's already confirmed, just get prize. If not, confirm first,
    // then get prize
    console.log("getAwardHandler");
    if (strategy?.isRequiredConfirmation) {
      await getAward();
      return;
    }

    const res = await claimReward();
    if (res === "COMPLETED" || res === "READY_AWARD") {
      const res = await getAward();
      if (res === "COMPLETED") {
        localStorage.removeItem("promo-code-task");
      }
    }
  };

  const handleAction = useCallback(async () => {
    if (!strategy) return;
    try {
      setIsProcessing(true);
      // await taskHandler.perform();
      if (strategy.shouldVerifyBeforePerform) {
        const res = await verifyTask(task);
        if (res === "COMPLETED" || res === "READY_AWARD") {
          setCurrentStatus(res);
          return;
        }
      }
      await strategy.perform({
        task,
        setStatus: setCurrentStatus,
        router,
        showStories,
        getDonAward: getAwardHandler,
        t,
      });
    } catch (error) {
      console.error("Task execution failed:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [router, showStories, strategy, task]);

  const statusFren = () => {
    if (!profileData) return TaskStatus.ReadyAward;
    return !task.user_process &&
      profileData.referral_count >= task.target_invite_count
      ? TaskStatus.ReadyAward
      : TaskStatus.Completed;
  };

  const renderButtonState = () => {
    if (task.coming_soon) {
      return null;
    }

    if (task.premium && !profileData?.is_premium) {
      return <PremiumAlertDialog />;
    }

    if (task.task_type === TaskVariant.Invites) {
      const textFrenOrFarm = !task.user_process
        ? t("task_status.READY_AWARD")
        : "";

      return (
        <>
          <TaskButton
            key={task.id}
            customText={textFrenOrFarm}
            status={statusFren()}
            onClick={getAwardHandler}
            showLoading={isClaiming || isVerifying}
          />
        </>
      );
    }

    if (task.task_type === TaskVariant.Farm) {
      const farmStatus = resolveFarmTaskStatus({
        localStatus: currentStatus,
        processStatus: task.user_process?.status,
        walletAmount: profileData?.wallet_amount,
        targetFarmCount: task.target_farm_count,
      });
      // Show the "claim" label only when the award is actually claimable.
      const textFrenOrFarm =
        farmStatus === TaskStatus.ReadyAward
          ? t("task_status.READY_AWARD")
          : "";

      return (
        <>
          <TaskButton
            key={task.id}
            customText={textFrenOrFarm}
            status={farmStatus}
            onClick={getAwardHandler}
            showLoading={isClaiming || isVerifying}
          />
        </>
      );
    }

    if (task.task_type === TaskVariant.Keyword) {
      return <PromocodeSheet task={task} getAward={getAward} />;
    }

    if (isProcessing || isClaiming || isVerifying) {
      return <TaskButton key={task.id} status="PENDING" showLoading />;
    }

    switch (currentStatus) {
      case "COMPLETED":
        return <TaskButton key={task.id} status="COMPLETED" />;
      case "READY_AWARD":
        return (
          <TaskButton
            key={task.id}
            status="READY_AWARD"
            onClick={getAwardHandler}
          />
        );
      case "PERFORMED":
        return (
          <TaskButton
            key={task.id}
            status="PERFORMED"
            onClick={() => {
              if (task.task_type === TaskVariant.Keyword) {
                router(`/get-don/${task?.id}/promo-code`);
              } else {
                claimReward();
              }
            }}
          />
        );
      default:
        return (
          <TaskButton
            key={task.id}
            status="INCOMPLETE"
            onClick={handleAction}
          />
        );
    }
  };

  return (
    <>
      {!isStoryLoading && storyData && (
        <TaskStoryModal
          ref={storiesModal}
          storiesData={[storyData]}
          task={task}
          getAwardFn={getAward}
          claimAwardFn={claimReward}
          setLoading={setIsProcessing}
        />
      )}
      {renderButtonState()}
    </>
  );
};

export default TaskActionButton;
